/**
 * DevAdminFeedbacks - 개발자용 피드백 관리 페이지
 *
 * - 전체 피드백 테이블 (유형, 제목, 작성자, 상태, 우선순위, 날짜)
 * - 상태/유형/우선순위별 필터
 * - 상세 뷰: 피드백 내용, 스크린샷, 환경정보 탭, 진단 데이터 탭
 * - 상태 변경, 우선순위 변경, 관리자 답변
 */

import { useEffect, useState } from 'react';

import {
  Bug, Lightbulb, ChevronDown, ChevronUp,
  Monitor, Activity, Send, Loader2, X,
  MessageSquare, Clock, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  collection, query, orderBy, limit, onSnapshot, doc, updateDoc, getDoc, Timestamp,
} from 'firebase/firestore';

import { AdminFire } from '../../services/SuperAdminFirebase';
import { SuperAdmin } from '../../services/SuperAdmin';
import type { Feedback, FeedbackStatus, FeedbackPriority, FeedbackMessage, ScreenshotEntry, FeedbackEnvironment, FeedbackDiagnostics } from '../../models/Feedback';
import { FEEDBACKS_COL } from '../../models/Feedback';
import { Uid } from '../../models/User';

const STATUS_LABELS_KO: Record<FeedbackStatus, string> = {
  draft: '작성중',
  submitted: '제출완료',
  reviewing: '검토중',
  resolving: '해결중',
  unresolved: '미해결',
  resolved: '해결완료',
};

const PRIORITY_LABELS_KO: Record<FeedbackPriority, string> = {
  low: '낮음',
  medium: '보통',
  high: '높음',
  critical: '긴급',
};

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  draft: 'bg-slate-500/10 text-slate-600',
  submitted: 'bg-blue-500/10 text-blue-600',
  reviewing: 'bg-yellow-500/10 text-yellow-600',
  resolving: 'bg-orange-500/10 text-orange-600',
  unresolved: 'bg-red-500/10 text-red-600',
  resolved: 'bg-green-500/10 text-green-600',
};

const PRIORITY_COLORS: Record<FeedbackPriority, string> = {
  low: 'text-slate-500',
  medium: 'text-blue-500',
  high: 'text-orange-500',
  critical: 'text-red-600 font-bold',
};

const ALL_STATUSES: FeedbackStatus[] = ['submitted', 'reviewing', 'resolving', 'unresolved', 'resolved'];
const ALL_PRIORITIES: FeedbackPriority[] = ['low', 'medium', 'high', 'critical'];

export default function DevAdminFeedbacks() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    const q = query(
      collection(AdminFire.store, FEEDBACKS_COL),
      orderBy('createdAt', 'desc'),
      limit(200),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          createdAt: data.createdAt?.toDate?.() ?? new Date(data.createdAt),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date(data.updatedAt),
          messages: (data.messages || []).map((m: any) => ({
            ...m,
            createdAt: m.createdAt?.toDate?.() ?? new Date(m.createdAt),
          })),
          screenshots: (data.screenshots || []).map((s: any) => ({
            ...s,
            capturedAt: s.capturedAt?.toDate?.() ?? new Date(s.capturedAt),
          })),
        } as Feedback;
      });
      // 관리자에게는 제출된 피드백만 표시 (draft 제외)
      setFeedbacks(list.filter(f => f.status !== 'draft'));
      setLoading(false);
    }, (error) => {
      console.error('DevAdminFeedbacks: onSnapshot error', error);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filtered = feedbacks.filter(fb => {
    if (filterStatus !== 'all' && fb.status !== filterStatus) return false;
    if (filterType !== 'all' && fb.type !== filterType) return false;
    return true;
  });

  const selected = selectedId ? feedbacks.find(f => f.id === selectedId) : null;

  return (
    <div className="flex h-full">
      {/* 목록 패널 */}
      <div className={`${selected ? 'w-1/2' : 'w-full'} border-r border-border flex flex-col transition-all`}>
        {/* 헤더 + 필터 */}
        <div className="p-4 border-b border-border space-y-3">
          <h1 className="text-xl font-bold">피드백 관리</h1>
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="상태" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체 상태</SelectItem>
                {ALL_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS_KO[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="유형" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체 유형</SelectItem>
                <SelectItem value="bug">🐛 버그</SelectItem>
                <SelectItem value="feature">✨ 기능 요청</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground self-center ml-auto">
              {filtered.length}건
            </span>
          </div>
        </div>

        {/* 테이블 */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <th className="text-left p-3">유형</th>
                  <th className="text-left p-3">제목</th>
                  <th className="text-left p-3">작성자</th>
                  <th className="text-left p-3">상태</th>
                  <th className="text-left p-3">우선순위</th>
                  <th className="text-left p-3">날짜</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(fb => (
                  <tr
                    key={fb.id}
                    onClick={() => setSelectedId(fb.id)}
                    className={`border-b border-border cursor-pointer hover:bg-muted/30 transition-colors ${selectedId === fb.id ? 'bg-primary/5' : ''}`}
                  >
                    <td className="p-3">
                      {fb.type === 'bug' ? <Bug className="w-4 h-4 text-red-500" /> : <Lightbulb className="w-4 h-4 text-yellow-500" />}
                    </td>
                    <td className="p-3 font-medium max-w-[200px]">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{fb.title}</span>
                        {fb.messages.length > 0 && (
                          <span className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600 text-meta-sm font-bold">
                            <MessageSquare className="w-3 h-3" /> {fb.messages.length}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-muted-foreground truncate max-w-[120px]">{fb.userName}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[fb.status]}`}>
                        {STATUS_LABELS_KO[fb.status]}
                      </span>
                    </td>
                    <td className={`p-3 text-xs ${PRIORITY_COLORS[fb.priority]}`}>
                      {PRIORITY_LABELS_KO[fb.priority]}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(fb.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 상세 패널 */}
      {selected && (
        <DetailPanel
          feedback={selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ── 상세 패널 ──
function DetailPanel({ feedback, onClose }: { feedback: Feedback; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<'content' | 'env' | 'diagnostics'>('content');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedScreenshot, setExpandedScreenshot] = useState<number | null>(null);

  const env = feedback.environment;
  const diag = feedback.diagnostics;

  const handleStatusChange = async (status: FeedbackStatus) => {
    await updateDoc(doc(AdminFire.store, FEEDBACKS_COL, feedback.id), {
      status, updatedAt: Timestamp.fromDate(new Date()),
    });
  };

  const handlePriorityChange = async (priority: FeedbackPriority) => {
    await updateDoc(doc(AdminFire.store, FEEDBACKS_COL, feedback.id), {
      priority, updatedAt: Timestamp.fromDate(new Date()),
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const fbDoc = await getDoc(doc(AdminFire.store, FEEDBACKS_COL, feedback.id));
      const fbData = fbDoc.data();
      const existingMsgs = (fbData?.messages || []);
      const newMsg = {
        id: Uid.get(),
        content: newMessage.trim(),
        authorId: SuperAdmin.id || 'admin',
        authorName: SuperAdmin.adminName || 'Admin',
        authorRole: 'admin',
        createdAt: Timestamp.fromDate(new Date()),
      };
      await updateDoc(doc(AdminFire.store, FEEDBACKS_COL, feedback.id), {
        messages: [...existingMsgs, newMsg],
        updatedAt: Timestamp.fromDate(new Date()),
      });
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send admin message:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-1/2 flex flex-col overflow-hidden">
      {/* 헤더 */}
      <div className="p-4 border-b border-border flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {feedback.type === 'bug' ? <Bug className="w-4 h-4 text-red-500" /> : <Lightbulb className="w-4 h-4 text-yellow-500" />}
            <h2 className="font-bold text-lg truncate">{feedback.title}</h2>
          </div>
          <div className="text-xs text-muted-foreground">
            {feedback.userName} ({feedback.userEmail}) · {new Date(feedback.createdAt).toLocaleString()}
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* 상태/우선순위 컨트롤 */}
      <div className="p-4 border-b border-border flex gap-3">
        <Select value={feedback.status} onValueChange={(v) => handleStatusChange(v as FeedbackStatus)}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALL_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{STATUS_LABELS_KO[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={feedback.priority} onValueChange={(v) => handlePriorityChange(v as FeedbackPriority)}>
          <SelectTrigger className="w-[120px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALL_PRIORITIES.map(p => (
              <SelectItem key={p} value={p}>{PRIORITY_LABELS_KO[p]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 탭 */}
      <div className="flex border-b border-border">
        {(['content', 'env', 'diagnostics'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'content' && <><MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />내용</>}
            {tab === 'env' && <><Monitor className="w-3.5 h-3.5 inline mr-1.5" />환경 정보</>}
            {tab === 'diagnostics' && <><Activity className="w-3.5 h-3.5 inline mr-1.5" />진단 데이터</>}
          </button>
        ))}
      </div>

      {/* 탭 내용 */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'content' && (
          <div className="p-4 space-y-4">
            {/* 설명 */}
            {feedback.description && (
              <div className="text-sm whitespace-pre-wrap">{feedback.description}</div>
            )}

            {/* 스크린샷 */}
            {feedback.screenshots.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-muted-foreground uppercase">스크린샷 ({feedback.screenshots.length})</h4>
                {feedback.screenshots.map((entry, i) => {
                  const isExpanded = expandedScreenshot === i;
                  return (
                    <div key={i} className="rounded-lg border border-border overflow-hidden">
                      {/* 썸네일 행 */}
                      <div className="flex items-center gap-3 p-2 hover:bg-muted/30 cursor-pointer" onClick={() => setExpandedScreenshot(isExpanded ? null : i)}>
                        <a href={entry.imageUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                          <img src={entry.imageUrl} alt="" className="w-24 h-16 object-cover rounded border border-border hover:border-primary/50 transition-colors" />
                        </a>
                        <div className="flex-1 min-w-0 text-xs">
                          {entry.platform && entry.platform !== 'unknown' ? (
                            <>
                              <div className="font-medium">{entry.platform} · {entry.environment?.browserInfo || '-'}</div>
                              <div className="text-muted-foreground truncate">{entry.currentUrl}</div>
                              <div className="text-muted-foreground">{new Date(entry.capturedAt).toLocaleString()}</div>
                            </>
                          ) : (
                            <div className="text-muted-foreground">캡처 #{i + 1}</div>
                          )}
                        </div>
                        {entry?.environment && (
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </div>
                      {/* 확장된 환경정보 & 진단데이터 */}
                      {isExpanded && (
                        <div className="border-t border-border bg-muted/20 pb-4">
                          {entry?.environment && (
                            <div className="pt-4 pl-4 pr-4">
                              <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">환경 정보</h5>
                              <FeedbackEnvData env={entry.environment} />
                            </div>
                          )}
                          {entry?.diagnostics && (
                            <div className="pt-4 pl-4 pr-4 border-t border-border mt-4">
                              <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">진단 데이터</h5>
                              <FeedbackDiagData diag={entry.diagnostics} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 메시지 스레드 */}
            <div className="space-y-2 pt-2 border-t border-border">
              <h4 className="text-xs font-medium text-muted-foreground uppercase">
                메시지 ({feedback.messages.length})
              </h4>
              {feedback.messages.map(msg => (
                <div key={msg.id} className={`rounded-lg p-3 text-sm ${
                  msg.authorRole === 'admin' ? 'bg-primary/5 border border-primary/20' :
                  msg.authorRole === 'ai' ? 'bg-muted border border-border' :
                  'bg-muted/50'
                }`}>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span className="font-medium">
                      {msg.authorRole === 'admin' && '🛡️ '}
                      {msg.authorRole === 'ai' && '🤖 '}
                      {msg.authorName}
                    </span>
                    <span>{new Date(msg.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))}

              {/* 관리자 답변 입력 */}
              <div className="flex gap-2 pt-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="관리자 답변..."
                  rows={2}
                  className="flex-1 resize-none text-sm"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="self-end"
                  size="sm"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'env' && env && (
          <div className="p-4">
            <FeedbackEnvData env={env} />
          </div>
        )}

        {activeTab === 'diagnostics' && diag && (
          <div className="p-4">
            <FeedbackDiagData diag={diag} />
          </div>
        )}
      </div>
    </div>
  );
}

function EnvSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground uppercase">{title}</div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

function EnvRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start px-3 py-1.5 text-xs">
      <span className="text-muted-foreground w-28 flex-shrink-0">{label}</span>
      <span className={`text-foreground break-all ${mono ? 'font-mono text-meta-sm' : ''}`}>{value}</span>
    </div>
  );
}

function FeedbackEnvData({ env }: { env: FeedbackEnvironment }) {
  return (
    <div className="space-y-3">
      <EnvSection title="디바이스 & 브라우저">
        <EnvRow label="플랫폼" value={env.platform} />
        <EnvRow label="브라우저" value={env.browserInfo} />
        <EnvRow label="OS" value={env.osInfo} />
        <EnvRow label="User Agent" value={env.userAgent} mono />
        <EnvRow label="언어" value={env.language} />
        <EnvRow label="터치스크린" value={env.touchScreen ? '예' : '아니오'} />
      </EnvSection>

      <EnvSection title="디스플레이">
        <EnvRow label="화면" value={`${env.screenWidth}×${env.screenHeight}`} />
        <EnvRow label="뷰포트" value={`${env.viewportWidth}×${env.viewportHeight}`} />
        <EnvRow label="픽셀 비율" value={`${env.devicePixelRatio}x`} />
      </EnvSection>

      <EnvSection title="앱 상태">
        <EnvRow label="앱 버전" value={env.appVersion} />
        <EnvRow label="OTA 번들" value={env.otaBundleVersion || 'N/A'} />
        <EnvRow label="URL" value={env.currentUrl} mono />
        <EnvRow label="로그인" value={env.isLoggedIn ? '예' : '아니오'} />
        <EnvRow label="워크스페이스" value={env.activeWorkspaceName || env.activeWorkspaceId || 'N/A'} />
        <EnvRow label="방" value={env.currentRoomId || 'N/A'} />
        <EnvRow label="PWA" value={env.isPWA ? '예' : '아니오'} />
        <EnvRow label="네이티브" value={env.isNativeApp ? '예' : '아니오'} />
      </EnvSection>

      <EnvSection title="성능 & 네트워크">
        <EnvRow label="온라인" value={env.onlineStatus ? '✅ 예' : '❌ 아니오'} />
        <EnvRow label="연결" value={env.connectionType || '알 수 없음'} />
        <EnvRow label="다운링크" value={env.effectiveDownlink != null ? `${env.effectiveDownlink} Mbps` : 'N/A'} />
        <EnvRow label="JS 힙 사용" value={env.jsHeapUsedMB != null ? `${env.jsHeapUsedMB} MB` : 'N/A'} />
        <EnvRow label="JS 힙 한도" value={env.jsHeapLimitMB != null ? `${env.jsHeapLimitMB} MB` : 'N/A'} />
        <EnvRow label="페이지 로드" value={env.pageLoadTimeMs != null ? `${env.pageLoadTimeMs} ms` : 'N/A'} />
      </EnvSection>
    </div>
  );
}

function FeedbackDiagData({ diag }: { diag: FeedbackDiagnostics }) {
  return (
    <div className="space-y-4">
      {/* JS 에러 */}
      {diag.jsErrors.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-red-500 uppercase mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            JS 에러 ({diag.jsErrors.length})
          </h4>
          <div className="space-y-2">
            {diag.jsErrors.map((err, i) => (
              <div key={i} className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-xs">
                <div className="font-medium text-red-600 mb-1">{err.message}</div>
                {err.source && <div className="text-muted-foreground">at {err.source}:{err.lineno}:{err.colno}</div>}
                {err.stack && (
                  <pre className="mt-2 text-meta-sm text-muted-foreground whitespace-pre-wrap overflow-x-auto max-h-24 overflow-y-auto">
                    {err.stack}
                  </pre>
                )}
                <div className="text-muted-foreground mt-1">{new Date(err.timestamp).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 네트워크 에러 */}
      {diag.networkErrors.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-orange-500 uppercase mb-2">
            네트워크 에러 ({diag.networkErrors.length})
          </h4>
          <div className="space-y-1">
            {diag.networkErrors.map((ne, i) => (
              <div key={i} className="text-xs font-mono border-b border-border pb-1">
                <span className="text-red-500">{ne.status}</span>{' '}
                <span className="text-muted-foreground">{ne.method}</span>{' '}
                <span className="truncate">{ne.url}</span>
                <span className="text-muted-foreground ml-2">{new Date(ne.timestamp).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 콘솔 로그 */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          콘솔 로그 ({diag.consoleLogs.length})
        </h4>
        <div className="rounded-lg border border-border bg-muted/30 p-2 max-h-80 overflow-y-auto font-mono text-meta-sm space-y-0.5">
          {diag.consoleLogs.map((log, i) => (
            <div key={i} className={`py-0.5 ${
              log.level === 'error' ? 'text-red-500 bg-red-500/5' :
              log.level === 'warn' ? 'text-yellow-600 bg-yellow-500/5' :
              'text-muted-foreground'
            }`}>
              <span className="opacity-50 mr-2">{new Date(log.timestamp).toLocaleTimeString()}</span>
              <span className="uppercase mr-2">[{log.level}]</span>
              {log.message.substring(0, 300)}
            </div>
          ))}
        </div>
      </div>

      {/* 수집 시간 */}
      <div className="text-xs text-muted-foreground">
        수집 시간: {new Date(diag.collectedAt).toLocaleString()}
      </div>
    </div>
  );
}
